/**
 * Format detector — magic-byte sniffer for disk image containers.
 *
 * By-magic detection, with a clearly-marked last resort. Every result carries
 * `basis`: 'magic' when the bytes said so, 'extension' when nothing matched and
 * only the filename suggested something. The header used to claim "never by
 * extension" while the tail branches did exactly that — so a random blob named
 * `x.bin` was reported as a `bin` image at 40%, with no way for the caller (or
 * the user) to tell a guess from a fact. Consumers should say so out loud.
 *
 * For ambiguous results,
 * `secondary` lists alternative interpretations the caller may want to probe.
 *
 * Surface: window.__DiskImage.detect(file, opts) → Promise<{
 *   format: 'iso9660'|'udf'|'iso9660+udf'|'vhd'|'vhdx'|'vmdk'|'qcow2'|'dmg'|'e01'|'mbr'|'gpt'|'bin'|'cue'|'raw'|'unknown',
 *   confidence: 0..1,
 *   secondary?: string[],
 *   detail: { ... format-specific },
 *   hint?: string  // free-text hint (e.g. "looks like VHD inside a sparse VMDK")
 * }>
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.detect) return;

    var BinaryReader = w.__DiskImage.BinaryReader;

    var ASCII = function (s) {
        var u = new Uint8Array(s.length);
        for (var i = 0; i < s.length; i++) u[i] = s.charCodeAt(i) & 0xFF;
        return u;
    };

    function _bytesEq(a, b, off) {
        off = off || 0;
        for (var i = 0; i < b.length; i++) if (a[off + i] !== b[i]) return false;
        return true;
    }

    function detect(file, opts) {
        opts = opts || {};
        var name = (file.name || '').toLowerCase();
        var size = file.size;
        var reader = new BinaryReader(file);

        // Read first 4 KB and last 512 B in parallel — covers most magic locations cheaply.
        var headP = reader.bytes(0, Math.min(4096, size));
        var tailP = (size >= 512) ? reader.bytes(size - 512, 512) : Promise.resolve(new Uint8Array(0));

        return Promise.all([headP, tailP]).then(function (parts) {
            var head = parts[0];
            var tail = parts[1];
            var detail = {};
            var secondary = [];

            // QCOW2: "QFI\xfb" at byte 0
            if (head.length >= 4 && head[0] === 0x51 && head[1] === 0x46 && head[2] === 0x49 && head[3] === 0xfb) {
                detail.version = (head[4] << 24) | (head[5] << 16) | (head[6] << 8) | head[7];
                return { format: 'qcow2', confidence: 1, detail: detail };
            }

            // VHDX: "vhdxfile" at byte 0
            if (head.length >= 8 && _bytesEq(head, ASCII('vhdxfile'))) {
                return { format: 'vhdx', confidence: 1, detail: detail };
            }

            // VMDK sparse: "KDMV" at byte 0
            if (head.length >= 4 && _bytesEq(head, ASCII('KDMV'))) {
                detail.subtype = 'sparse';
                return { format: 'vmdk', confidence: 1, detail: detail };
            }
            // VMDK COWD legacy
            if (head.length >= 4 && _bytesEq(head, ASCII('COWD'))) {
                detail.subtype = 'cowd';
                return { format: 'vmdk', confidence: 0.9, detail: detail };
            }
            // VMDK descriptor (text-mode): starts with "# Disk DescriptorFile" or has "createType"
            if (head.length >= 64) {
                var asAscii = '';
                for (var i = 0; i < Math.min(head.length, 1024); i++) asAscii += String.fromCharCode(head[i]);
                if (asAscii.indexOf('# Disk DescriptorFile') === 0 ||
                    asAscii.indexOf('createType') >= 0 && asAscii.indexOf('# Disk') >= 0) {
                    detail.subtype = 'descriptor';
                    return { format: 'vmdk', confidence: 0.95, detail: detail };
                }
            }

            // E01 (EWF): "EVF\x09\x0d\x0a\xff\x00" at byte 0
            if (head.length >= 8 && head[0] === 0x45 && head[1] === 0x56 && head[2] === 0x46 &&
                head[3] === 0x09 && head[4] === 0x0d && head[5] === 0x0a && head[6] === 0xff && head[7] === 0x00) {
                return { format: 'e01', confidence: 1, detail: detail };
            }

            // VHD footer at last 512 bytes: "conectix" at offset 0 of the footer
            if (tail.length >= 8 && _bytesEq(tail, ASCII('conectix'))) {
                detail.diskType = (tail[60] << 24) | (tail[61] << 16) | (tail[62] << 8) | tail[63];
                return { format: 'vhd', confidence: 1, detail: detail };
            }

            // DMG: "koly" trailer at last 512 (offset 0 of trailer)
            if (tail.length >= 4 && _bytesEq(tail, ASCII('koly'))) {
                return { format: 'dmg', confidence: 1, detail: detail };
            }

            // ISO9660 / UDF detection — needs sector 16 (offset 0x8000)
            // Volume Recognition Sequence: descriptors at sectors 16+ until terminator
            // ISO9660: byte 0=type(0..3, 0xff=terminator), bytes 1..5 = "CD001"
            // UDF: bytes 1..5 = "BEA01" / "NSR02"|"NSR03" / "BOOT2" / "TEA01"
            if (size >= 0x8006) {
                return reader.bytes(0x8001, 5).then(function (s16) {
                    var s16ascii = String.fromCharCode(s16[0], s16[1], s16[2], s16[3], s16[4]);
                    detail.iso_at_16 = s16ascii;

                    // Probe sector 17, 18, 19 for UDF NSR / TEA
                    var nsrP = reader.bytes(0x8801, 5).catch(function () { return null; });    // sector 17 +1
                    var nsr2P = reader.bytes(0x9001, 5).catch(function () { return null; });   // sector 18 +1
                    var nsr3P = reader.bytes(0x9801, 5).catch(function () { return null; });   // sector 19 +1
                    return Promise.all([nsrP, nsr2P, nsr3P]).then(function (probes) {
                        var udfHit = false;
                        var udfMarkers = [];
                        probes.forEach(function (p) {
                            if (!p) return;
                            var s = String.fromCharCode(p[0], p[1], p[2], p[3], p[4]);
                            if (s === 'NSR02' || s === 'NSR03') { udfHit = true; udfMarkers.push(s); }
                            else if (s === 'BEA01' || s === 'TEA01' || s === 'BOOT2') udfMarkers.push(s);
                        });
                        detail.udf_markers = udfMarkers;

                        var isoHit = (s16ascii === 'CD001');

                        if (udfHit && isoHit) return { format: 'iso9660+udf', confidence: 1, detail: detail, secondary: ['iso9660', 'udf'] };
                        if (udfHit) return { format: 'udf', confidence: 1, detail: detail };
                        if (isoHit) return { format: 'iso9660', confidence: 1, detail: detail };

                        // No ISO/UDF — try MBR/GPT/raw
                        return _detectPartitionTable(reader, head, tail, name, size, detail, secondary);
                    });
                });
            }

            return _detectPartitionTable(reader, head, tail, name, size, detail, secondary);
        }).catch(function (err) {
            return { format: 'unknown', confidence: 0, detail: { error: String(err && err.message || err) } };
        });
    }

    function _detectPartitionTable(reader, head, tail, name, size, detail, secondary) {
        // GPT primary at LBA 1 (sector 512–1023)
        // GPT signature "EFI PART" at offset 512
        var gptP = (size >= 1024) ? reader.bytes(512, 8).then(function (sig) {
            return sig[0] === 0x45 && sig[1] === 0x46 && sig[2] === 0x49 && sig[3] === 0x20 &&
                   sig[4] === 0x50 && sig[5] === 0x41 && sig[6] === 0x52 && sig[7] === 0x54;
        }).catch(function () { return false; }) : Promise.resolve(false);

        return gptP.then(function (isGpt) {
            // MBR signature 0x55 0xAA at offset 510
            var hasMbrSig = (head.length >= 512 && head[510] === 0x55 && head[511] === 0xAA);

            if (isGpt) {
                detail.has_protective_mbr = hasMbrSig;
                return { format: 'gpt', confidence: 1, detail: detail, secondary: hasMbrSig ? ['mbr'] : [] };
            }
            if (hasMbrSig) {
                // BIN/CUE: image is paired with a .cue text file; here we just have a possible BIN
                // BIN data sectors usually start with "MM 00 00 ... 00 FF FF FF FF FF FF FF FF FF FF" (sync pattern)
                var sync = (head.length >= 12 && head[0] === 0x00 && head[1] === 0xFF && head[2] === 0xFF &&
                            head[3] === 0xFF && head[4] === 0xFF && head[5] === 0xFF && head[6] === 0xFF &&
                            head[7] === 0xFF && head[8] === 0xFF && head[9] === 0xFF && head[10] === 0xFF && head[11] === 0x00);
                if (sync || /\.bin$/.test(name)) {
                    detail.has_mbr = true;
                    return { format: 'bin', confidence: 0.7, detail: detail, secondary: ['mbr', 'raw'] };
                }
                return { format: 'mbr', confidence: 0.8, detail: detail };
            }

            // CUE: text file with TRACK/INDEX
            if (/\.cue$/.test(name) && head.length >= 16) {
                var s = '';
                for (var i = 0; i < Math.min(head.length, 512); i++) s += String.fromCharCode(head[i]);
                if (/\b(?:TRACK|INDEX|FILE)\b/i.test(s)) {
                    return { format: 'cue', confidence: 1, detail: { sample: s.slice(0, 256) } };
                }
            }

            // BIN/IMG/IMA fallback by sync pattern
            if (head.length >= 12 && head[0] === 0x00 && head[1] === 0xFF && head[2] === 0xFF) {
                detail.has_sync_pattern = true;
                return { format: 'bin', confidence: 0.6, detail: detail, secondary: ['raw'] };
            }
            // Last resort: the bytes matched nothing. Only the NAME suggests a
            // format, so label the result as such.
            if (/\.(?:img|ima|raw|dd)$/.test(name)) {
                return { format: 'raw', confidence: 0.5, detail: detail, basis: 'extension' };
            }
            if (/\.(?:bin)$/.test(name)) {
                return { format: 'bin', confidence: 0.4, detail: detail, basis: 'extension' };
            }

            return { format: 'unknown', confidence: 0, detail: detail, basis: 'none' };
        });
    }

    // Everything that did not set `basis` recognised real magic bytes.
    var _detect = detect;
    detect = function (file) {
        return _detect(file).then(function (r) {
            if (r && !r.basis) r.basis = 'magic';
            return r;
        });
    };

    w.__DiskImage.detect = detect;
})(window);
